import { useRef, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import gsap from 'gsap'
import { UP, STAGGER, SPRING } from '@/lib/motion'

export function Hero() {
  const sectionRef = useRef<HTMLElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  })

  // Parallax transforms for ASHU letters
  const yA = useTransform(scrollYProgress, [0, 1], [0, -30])
  const yS = useTransform(scrollYProgress, [0, 1], [0, -50])
  const yH = useTransform(scrollYProgress, [0, 1], [0, -70])
  const yU = useTransform(scrollYProgress, [0, 1], [0, -90])

  useEffect(() => {
    // Animate grid background
    if (gridRef.current) {
      gsap.to(gridRef.current, {
        backgroundPosition: '60px 60px',
        duration: 4,
        repeat: -1,
        ease: 'none'
      })
    }

    return () => {
      gsap.killTweensOf(gridRef.current)
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      style={{
        position: 'relative',
        minHeight: '100svh',
        background: 'var(--black)',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: '1fr',
        gridTemplateColumns: '1fr'
      }}
    >
      {/* LAYER 1: ANIMATED BACKGROUND GRID */}
      <div
        ref={gridRef}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(232,255,0,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(232,255,0,0.06) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Radial gradient orb */}
      <motion.div
        style={{
          position: 'absolute',
          width: '80vw',
          height: '80vw',
          maxWidth: 900,
          maxHeight: 900,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,255,0,0.12) 0%, transparent 70%)',
          top: '50%',
          left: '50%',
          x: '-50%',
          y: '-50%',
          filter: 'blur(40px)'
        }}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.8, 1, 0.8]
        }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* LAYER 2: GIANT TEXT LETTERS */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 1
        }}
      >
        <div
          style={{
            fontFamily: 'Bebas Neue',
            fontSize: 'clamp(20vw, 28vw, 380px)',
            lineHeight: 0.85,
            letterSpacing: '-0.02em',
            color: 'transparent',
            WebkitTextStroke: '1px rgba(232,255,0,0.15)',
            userSelect: 'none',
            display: 'flex',
            gap: '0.02em'
          }}
        >
          {/* Each letter individually animated with parallax */}
          <motion.span
            style={{
              display: 'inline-block',
              color: 'rgba(232,255,0,0.04)',
              WebkitTextStroke: '1px rgba(232,255,0,0.12)',
              textShadow: '0 0 80px rgba(232,255,0,0.08)'
            }}
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 40, damping: 10, delay: 0.1 }}
          >
            <motion.span style={{ y: yA }}>A</motion.span>
          </motion.span>
          <motion.span
            style={{
              display: 'inline-block',
              color: 'rgba(232,255,0,0.04)',
              WebkitTextStroke: '1px rgba(232,255,0,0.12)',
              textShadow: '0 0 80px rgba(232,255,0,0.08)'
            }}
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 40, damping: 10, delay: 0.18 }}
          >
            <motion.span style={{ y: yS }}>S</motion.span>
          </motion.span>
          <motion.span
            style={{
              display: 'inline-block',
              color: 'rgba(232,255,0,0.04)',
              WebkitTextStroke: '1px rgba(232,255,0,0.12)',
              textShadow: '0 0 80px rgba(232,255,0,0.08)'
            }}
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 40, damping: 10, delay: 0.26 }}
          >
            <motion.span style={{ y: yH }}>H</motion.span>
          </motion.span>
          <motion.span
            style={{
              display: 'inline-block',
              color: 'rgba(232,255,0,0.04)',
              WebkitTextStroke: '1px rgba(232,255,0,0.12)',
              textShadow: '0 0 80px rgba(232,255,0,0.08)'
            }}
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 40, damping: 10, delay: 0.34 }}
          >
            <motion.span style={{ y: yU }}>U</motion.span>
          </motion.span>
        </div>
      </div>

      {/* LAYER 3: CONTENT */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          alignItems: 'center',
          padding: '120px max(5vw, 24px) 80px',
          minHeight: '100svh',
          gap: 40
        }}
      >
        {/* LEFT: Main content */}
        <motion.div
          variants={STAGGER(0.1)}
          initial="hidden"
          animate="show"
          style={{ display: 'flex', flexDirection: 'column', gap: 28 }}
        >
          {/* Eyebrow */}
          <motion.div
            variants={UP}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              width: 'fit-content',
              padding: '8px 18px',
              border: '1px solid rgba(232,255,0,0.3)',
              background: 'rgba(232,255,0,0.07)',
              borderRadius: 'var(--radius-full)'
            }}
          >
            <motion.span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--lime)'
              }}
              animate={{ opacity: [1, 0.3, 1], scale: [1, 0.8, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span
              style={{
                fontFamily: 'DM Sans',
                fontWeight: 600,
                fontSize: '0.8rem',
                color: 'var(--lime)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase'
              }}
            >
              Professional Cleaning · Addis Ababa
            </span>
          </motion.div>

          {/* Headline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Line 1: ADDIS */}
            <div style={{ overflow: 'hidden' }}>
              <motion.div
                style={{
                  fontFamily: 'Bebas Neue',
                  fontSize: 'var(--display)',
                  color: 'var(--white)',
                  lineHeight: 0.9,
                  letterSpacing: '-0.01em'
                }}
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ ...SPRING, delay: 0.2 }}
              >
                ADDIS
              </motion.div>
            </div>

            {/* Line 2: ABABA'S */}
            <div style={{ overflow: 'hidden' }}>
              <motion.div
                style={{
                  fontFamily: 'Bebas Neue',
                  fontSize: 'var(--display)',
                  color: 'transparent',
                  WebkitTextStroke: '2px var(--lime)',
                  lineHeight: 0.9,
                  letterSpacing: '-0.01em'
                }}
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ ...SPRING, delay: 0.28 }}
              >
                ABABA'S
              </motion.div>
            </div>

            {/* Line 3: CLEANEST */}
            <div style={{ overflow: 'hidden' }}>
              <motion.div
                style={{
                  fontFamily: 'Bebas Neue',
                  fontSize: 'var(--display)',
                  color: 'var(--lime)',
                  lineHeight: 0.9,
                  letterSpacing: '-0.01em',
                  textShadow: '0 0 60px rgba(232,255,0,0.5)'
                }}
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ ...SPRING, delay: 0.36 }}
              >
                CLEANEST.
              </motion.div>
            </div>
          </div>

          {/* Amharic line */}
          <motion.div
            variants={UP}
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 400,
              fontSize: '1.1rem',
              color: 'rgba(255,255,255,0.45)',
              letterSpacing: '0.02em'
            }}
          >
            አሹ — ቤትዎ ያምርበት። ሁሌም።
          </motion.div>

          {/* Description */}
          <motion.p
            variants={UP}
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: 1.75,
              color: 'var(--white-60)',
              maxWidth: 480
            }}
          >
            Residential · Commercial · Deep Clean. Sofa washing · Mattress · Post-construction.
            Call us and we handle the rest.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={UP} style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <motion.a
              href="tel:+251997745317"
              data-cursor="hover"
              whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(232,255,0,0.5)' }}
              whileTap={{ scale: 0.96 }}
              style={{
                background: 'var(--lime)',
                color: 'var(--black)',
                fontFamily: 'DM Sans',
                fontWeight: 800,
                fontSize: '1rem',
                padding: '16px 36px',
                borderRadius: 'var(--radius-full)',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              animate={{
                boxShadow: [
                  '0 0 0px rgba(232,255,0,0)',
                  '0 0 40px rgba(232,255,0,0.45)',
                  '0 0 0px rgba(232,255,0,0)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              📞 Book Now · ያዝ
            </motion.a>

            <motion.a
              href="/services"
              data-cursor="hover"
              whileHover={{
                borderColor: 'var(--lime)',
                color: 'var(--lime)'
              }}
              style={{
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'var(--white)',
                fontFamily: 'DM Sans',
                fontWeight: 600,
                fontSize: '1rem',
                padding: '16px 36px',
                borderRadius: 'var(--radius-full)',
                textDecoration: 'none',
                transition: 'all 0.3s'
              }}
            >
              Our Services ↓
            </motion.a>
          </motion.div>

          {/* Stats row */}
          <motion.div
            variants={UP}
            style={{
              display: 'flex',
              gap: 36,
              paddingTop: 8,
              borderTop: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            {[
              { val: '500+', label: 'Homes Cleaned' },
              { val: '24/7', label: 'Available' },
              { val: '100%', label: 'Guaranteed' }
            ].map((s, i) => (
              <motion.div
                key={s.val}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.1, ...SPRING }}
              >
                <div
                  style={{
                    fontFamily: 'Bebas Neue',
                    fontSize: '2.2rem',
                    color: 'var(--lime)',
                    lineHeight: 1
                  }}
                >
                  {s.val}
                </div>
                <div
                  style={{
                    fontFamily: 'DM Sans',
                    fontSize: '0.78rem',
                    color: 'var(--white-30)',
                    marginTop: 2
                  }}
                >
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* RIGHT: Animated visual system */}
        <div
          style={{
            position: 'relative',
            height: '70vh',
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Orbit ring 1 */}
          <motion.div
            style={{
              position: 'absolute',
              width: 420,
              height: 420,
              border: '1px solid rgba(232,255,0,0.08)',
              borderRadius: '50%'
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />

          {/* Orbit ring 2 */}
          <motion.div
            style={{
              position: 'absolute',
              width: 280,
              height: 280,
              border: '1px dashed rgba(232,255,0,0.12)',
              borderRadius: '50%'
            }}
            animate={{ rotate: -360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />

          {/* Center glow */}
          <motion.div
            style={{
              position: 'absolute',
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(232,255,0,0.2) 0%, transparent 70%)',
              filter: 'blur(20px)'
            }}
            animate={{ scale: [0.9, 1.1, 0.9], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Central SVG: Spray bottle */}
          <svg viewBox="0 0 200 320" width="180" style={{ position: 'relative', zIndex: 2 }}>
            {/* Bottle body */}
            <rect
              x="60"
              y="80"
              width="80"
              height="180"
              rx="16"
              fill="none"
              stroke="rgba(232,255,0,0.7)"
              strokeWidth="1.5"
            />
            {/* Trigger mechanism */}
            <path
              d="M 60 140 Q 30 130 25 115 Q 20 100 40 95 L 60 100"
              fill="none"
              stroke="rgba(232,255,0,0.7)"
              strokeWidth="1.5"
            />
            {/* Nozzle */}
            <line
              x1="25"
              y1="95"
              x2="0"
              y2="75"
              stroke="rgba(232,255,0,0.7)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            {/* Spray particles */}
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <motion.circle
                key={i}
                cx={-10 - i * 8}
                cy={75 + (i % 2 === 0 ? -5 : 5)}
                r={1.5}
                fill="var(--lime)"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 0.8, 0], scale: [0, 1, 0.5], x: -30 - i * 10 }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: i * 0.15,
                  ease: 'easeInOut'
                }}
              />
            ))}
            {/* Label area */}
            <rect
              x="72"
              y="130"
              width="56"
              height="80"
              rx="8"
              fill="rgba(232,255,0,0.06)"
              stroke="rgba(232,255,0,0.2)"
              strokeWidth="1"
            />
            <text
              x="100"
              y="168"
              textAnchor="middle"
              fontFamily="Bebas Neue"
              fontSize="18"
              fill="rgba(232,255,0,0.8)"
            >
              ASHU
            </text>
            {/* Shine */}
            <ellipse
              cx="76"
              cy="110"
              rx="8"
              ry="20"
              fill="rgba(255,255,255,0.06)"
              transform="rotate(-15,76,110)"
            />
          </svg>

          {/* Floating stat cards */}
          {[
            { label: 'ቤት / Home', top: '5%', right: '0%', rotate: -6 },
            { label: '★★★★★', bottom: '15%', left: '-5%', rotate: 4 },
            { label: 'አሹ ✓', top: '45%', right: '-8%', rotate: -3 }
          ].map((card, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                ...card,
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(232,255,0,0.15)',
                borderRadius: 14,
                backdropFilter: 'blur(12px)',
                fontFamily: 'DM Sans',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: 'var(--white)',
                rotate: card.rotate,
                whiteSpace: 'nowrap'
              }}
              animate={{
                y: [0, -10 - i * 4, 0],
                rotate: [card.rotate, card.rotate + 2, card.rotate]
              }}
              transition={{
                duration: 3.5 + i * 0.8,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
            >
              {card.label}
            </motion.div>
          ))}

          {/* Lime particles floating up */}
          {Array.from({ length: 16 }).map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: 3 + Math.random() * 5,
                height: 3 + Math.random() * 5,
                borderRadius: '50%',
                background: 'var(--lime)',
                left: `${20 + Math.random() * 60}%`,
                bottom: `${Math.random() * 40}%`,
                opacity: 0.3 + Math.random() * 0.4
              }}
              animate={{
                y: [0, -(80 + Math.random() * 120)],
                opacity: [0.4, 0],
                x: (Math.random() - 0.5) * 40
              }}
              transition={{
                duration: 3 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 4,
                ease: 'easeOut'
              }}
            />
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: 32,
          left: '50%',
          x: '-50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          zIndex: 3
        }}
      >
        <motion.div
          style={{
            width: 24,
            height: 40,
            border: '1px solid rgba(232,255,0,0.3)',
            borderRadius: 12,
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 6
          }}
        >
          <motion.div
            style={{
              width: 3,
              height: 8,
              borderRadius: 2,
              background: 'var(--lime)'
            }}
            animate={{ y: [0, 14, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
        </motion.div>
        <span
          style={{
            fontFamily: 'DM Sans',
            fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.25)',
            letterSpacing: '0.2em',
            textTransform: 'uppercase'
          }}
        >
          scroll
        </span>
      </motion.div>
    </section>
  )
}
